import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError } from '@/lib/api/client'
import { landingFetchDonorMe } from '@/lib/landing-auth'
import { establishLandingDonorSession } from '@/lib/landing-donor-session'
import {
  authorizeDonorSso,
  canAuthorizeDonorSso,
  canStartDonorSso,
  exchangeDonorSso,
  previewDonorSso,
  startDonorSso,
  stripDonorSsoCallbackParams,
  type DonorSsoPreview,
} from '@/lib/landing-donor-sso'

type Options = {
  slug?: string
  searchParams: URLSearchParams
  onAuthenticated: (path: string) => void
  onError: (message: string) => void
}

export function useLandingDonorSso({
  slug,
  searchParams,
  onAuthenticated,
  onError,
}: Options) {
  const [pending, setPending] = useState(false)
  const [confirmation, setConfirmation] = useState<
    (DonorSsoPreview & { requestToken: string }) | null
  >(null)
  const previewRef = useRef<{ requestToken: string; preview: DonorSsoPreview } | null>(
    null
  )
  const requestToken =
    !slug && canAuthorizeDonorSso() ? (searchParams.get('sso_request') ?? '') : ''
  const code = searchParams.get('sso_code') ?? ''
  const state = searchParams.get('sso_state') ?? ''
  const isCallback = searchParams.has('sso_code') || searchParams.has('sso_state')
  const operation = useRef<{
    key: string
    promise: Promise<{
      path?: string
      destination?: string
      confirmation?: DonorSsoPreview
    }>
  } | null>(null)
  const starting = useRef(false)

  const finishSignIn = useCallback(async () => {
    if (!requestToken) return false
    const preview =
      previewRef.current?.requestToken === requestToken
        ? previewRef.current.preview
        : await previewDonorSso(requestToken)
    previewRef.current = { requestToken, preview }
    if (preview.requires_confirmation) {
      setConfirmation({ ...preview, requestToken })
      return true
    }
    const destination = await authorizeDonorSso(requestToken)
    window.location.assign(destination)
    return true
  }, [requestToken])

  const confirmSignIn = useCallback(async () => {
    if (!confirmation || pending) return
    setPending(true)
    try {
      window.location.assign(await authorizeDonorSso(confirmation.requestToken, true))
    } catch {
      onError('Não foi possível continuar o acesso. Abra a campanha e tente novamente.')
    } finally {
      setPending(false)
    }
  }, [confirmation, onError, pending])

  const cancelSignIn = useCallback(() => {
    setConfirmation(null)
    onAuthenticated('/conta')
  }, [onAuthenticated])

  const start = useCallback(
    async (campaignId: number) => {
      if (!slug || isCallback || starting.current || !canStartDonorSso()) return false
      starting.current = true
      setPending(true)
      try {
        window.location.assign(await startDonorSso(campaignId, slug, searchParams))
        return true
      } catch {
        setPending(false)
        onError(
          'Não foi possível abrir o acesso da Sortee. Você pode entrar com sua conta abaixo.'
        )
        return false
      }
    },
    [isCallback, onError, searchParams, slug]
  )

  useEffect(() => {
    if (!isCallback && !requestToken) return
    let active = true
    const key = `${code}|${state}|${requestToken}|${slug ?? ''}`
    if (operation.current?.key !== key) {
      const promise = (async () => {
        if (isCallback) {
          stripDonorSsoCallbackParams()
          if (!slug) throw new Error('Campanha inválida.')
          const path = await exchangeDonorSso(code, state, slug)
          if (!(await establishLandingDonorSession()))
            throw new Error('Sessão indisponível.')
          return { path }
        }
        const preview = await previewDonorSso(requestToken)
        previewRef.current = { requestToken, preview }
        await landingFetchDonorMe('cookie')
        if (preview.requires_confirmation) return { confirmation: preview }
        return { destination: await authorizeDonorSso(requestToken) }
      })()
      operation.current = { key, promise }
    }
    queueMicrotask(() => {
      if (active) setPending(true)
    })
    void operation.current.promise
      .then((result) => {
        if (!active) return
        if (result.confirmation) setConfirmation({ ...result.confirmation, requestToken })
        else if (result.path) onAuthenticated(result.path)
        else if (result.destination) window.location.assign(result.destination)
      })
      .catch((error: unknown) => {
        if (
          active &&
          (isCallback || !(error instanceof ApiClientError && error.status === 401))
        ) {
          onError(
            'Não foi possível confirmar seu acesso. Entre novamente para continuar.'
          )
        }
      })
      .finally(() => {
        if (active) setPending(false)
      })
    return () => {
      active = false
    }
  }, [code, isCallback, onAuthenticated, onError, requestToken, slug, state])

  return {
    pending,
    isCallback,
    start,
    finishSignIn,
    confirmation,
    confirmSignIn,
    cancelSignIn,
  }
}
